import ApplicationController from '../../../javascript/controllers/application_controller'
import Sortable from 'sortablejs'
import { patch } from '@rails/request.js'

export default class extends ApplicationController {
  static targets = ['column', 'count']
  static values = { updateUrl: String, dropTargetClasses: String }

  #frameLoadHandler

  connect() {
    this.#frameLoadHandler = (event) => {
      this.#initializeSortables()
      this.#updateCountAfterLoad(event.target)
    }
    this.#initializeSortables()
    this.element.addEventListener('turbo:frame-load', this.#frameLoadHandler)
  }

  disconnect() {
    this.element.removeEventListener('turbo:frame-load', this.#frameLoadHandler)
  }

  handleFrameError(event) {
    event.preventDefault()
    const frame = event.target
    frame.innerHTML =
      '<p class="p-2 text-sm text-red-500">Failed to load. Reload the page to try again.</p>'
  }

  #initializeSortables() {
    this.columnTargets.forEach((column) => {
      if (column.sortable) return

      column.sortable = Sortable.create(column, {
        group: 'kanban',
        animation: 150,
        draggable: '[draggable]',
        ghostClass: 'opacity-50',
        onStart: (event) => {
          event.item.classList.remove('cursor-grab')
          event.item.classList.add('cursor-grabbing')
          event.item._revertBefore = event.from.children[event.oldIndex + 1] || null
          this.#highlightDropTargets(event.from)
        },
        onEnd: (event) => {
          event.item.classList.remove('cursor-grabbing')
          event.item.classList.add('cursor-grab')
          this.#removeDropTargetHighlights()
          this.#handleDrop(event)
        },
      })
    })
  }

  get #dropTargetClassList() {
    return this.dropTargetClassesValue.split(' ').filter(Boolean)
  }

  #highlightDropTargets(sourceColumn) {
    this.columnTargets.forEach((column) => {
      if (column !== sourceColumn) {
        column.classList.add(...this.#dropTargetClassList)
      }
    })
  }

  #removeDropTargetHighlights() {
    this.columnTargets.forEach((column) => {
      column.classList.remove(...this.#dropTargetClassList)
    })
  }

  /**
   * Updates the column header count after a turbo frame loads.
   * Uses data-total-count from the server when available (accounts for preview limit),
   * falling back to DOM counting for backwards compatibility.
   * @param {HTMLElement} frame - The turbo frame that just loaded
   */
  #updateCountAfterLoad(frame) {
    const column = frame.querySelector(`[data-${this.identifier}-target="column"]`)
    if (!column) return

    const index = this.columnTargets.indexOf(column)
    if (index >= 0 && this.countTargets[index]) {
      const totalCount = column.dataset.totalCount
      this.countTargets[index].textContent = totalCount
        ? `(${totalCount})`
        : `(${column.querySelectorAll('[draggable]').length})`
    }
  }

  /**
   * Updates column header counts after a drag-and-drop operation.
   * Uses data-total-count when available (accounts for preview limit),
   * falling back to DOM counting.
   */
  #updateColumnCounts(fromColumn, toColumn) {
    if (fromColumn === toColumn) return

    const fromIndex = this.columnTargets.indexOf(fromColumn)
    const toIndex = this.columnTargets.indexOf(toColumn)

    if (fromIndex >= 0 && this.countTargets[fromIndex]) {
      const fromTotal = parseInt(fromColumn.dataset.totalCount, 10)
      if (!isNaN(fromTotal)) {
        fromColumn.dataset.totalCount = String(fromTotal - 1)
        this.countTargets[fromIndex].textContent = `(${fromTotal - 1})`
      } else {
        this.countTargets[fromIndex].textContent =
          `(${fromColumn.querySelectorAll('[draggable]').length})`
      }
    }
    if (toIndex >= 0 && this.countTargets[toIndex]) {
      const toTotal = parseInt(toColumn.dataset.totalCount, 10)
      if (!isNaN(toTotal)) {
        toColumn.dataset.totalCount = String(toTotal + 1)
        this.countTargets[toIndex].textContent = `(${toTotal + 1})`
      } else {
        this.countTargets[toIndex].textContent =
          `(${toColumn.querySelectorAll('[draggable]').length})`
      }
    }
  }

  async #handleDrop(event) {
    if (event.from === event.to && event.oldIndex === event.newIndex) return

    const taskId = event.item.dataset.taskId
    const columnStatus = event.to.dataset.status
    const newStatus = columnStatus === 'closed' ? 'done' : columnStatus
    const url = this.updateUrlValue.replace('__TASK_ID__', taskId)

    try {
      const response = await patch(url, {
        body: JSON.stringify({ task: { status: newStatus } }),
        contentType: 'application/json',
        responseKind: 'turbo-stream',
      })
      if (response.ok) {
        this.#updateColumnCounts(event.from, event.to)
      } else {
        this.#revertDrop(event)
      }
    } catch (error) {
      this.reportError(error, { action: 'handleDrop', taskId, targetStatus: newStatus })
      this.#revertDrop(event)
    }
  }

  #revertDrop(event) {
    event.from.insertBefore(event.item, event.item._revertBefore)
    delete event.item._revertBefore
  }
}
